import { __awaiter, __decorate } from "tslib";
import { Connect, dataServerResultFilter, Datasource, DatasourceDefaults, MessageType, normaliseCriteria, } from '@genesislcap/foundation-comms';
import { attr } from '@microsoft/fast-element';
import { FoundationElement } from '@microsoft/fast-foundation';
import { BehaviorSubject } from 'rxjs';
import { logger } from './logger';
const criteriaDelimiter = ';';
const criteriaJoin = ' && ';
const withoutFullInit = null;
export class BaseDatasource extends FoundationElement {
    constructor() {
        super(...arguments);
        this.withGridInit = true;
        this.isSnapshot = false;
        this.maxRows = DatasourceDefaults.MAX_ROWS_250;
        this.maxView = DatasourceDefaults.MAX_VIEW_1000;
        this.reverse = false;
        this.rowId = 'ROW_REF';
        /**
         * Enabled by default, will format UNIX timestamp fields to readable [DATE|DATETIME] values.
         * @public
         */
        this.withTimestampFormatting = true;
        this.requiresFullDataRefresh = true;
        this.rowsSyncedWithStream = new Map();
        this.dataSubWasLoggedOff = false;
        this.criteriaFromFilters = new Map();
        this.update = new BehaviorSubject(new Map());
    }
    criteriaChanged(oldCriteria, newCriteria) {
        const criteriaIsNotDuplicate = oldCriteria !== normaliseCriteria(newCriteria, criteriaDelimiter);
        if (this.datasource.initialized && criteriaIsNotDuplicate) {
            if (this.dataSub) {
                this.dataSub.unsubscribe();
                this.dataSub = undefined;
            }
            this.fetchGenesisData(withoutFullInit);
        }
    }
    set datasourceConfig(config) {
        if (config) {
            Object.keys(config).forEach((key) => {
                if (config[key] !== undefined) {
                    this[key] = config[key];
                }
            });
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.clearData();
    }
    // Genesis Specific
    reset() {
        this.requiresFullDataRefresh = true;
        this.fetchGenesisData();
    }
    fetchGenesisData(withFullInit = true) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.data) {
                this.syncComponentData(this.data);
                return;
            }
            const requiresMetadataFetch = withFullInit || !this.datasource.initialized;
            if (!requiresMetadataFetch) {
                this.connect.dataLogoff(this.sourceRef);
                this.clearData();
            }
            const initOK = yield this.datasource.init(this.datasourceOptions(), requiresMetadataFetch);
            if (!initOK) {
                logger.debug(`Genesis Datasource init failed for ${this.resourceName}`);
                return;
            }
            if (this.isSnapshot) {
                const rowData = yield this.datasource.snapshotFiltered();
                this.syncComponentData(rowData);
                return;
            }
            logger.debug(`requesting stream for ${this.resourceName}`);
            this.dataSub = this.datasource.stream.subscribe((result) => {
                this.sourceRef = result.SOURCE_REF;
                const messageType = result.MESSAGE_TYPE;
                if (messageType && messageType === MessageType.LOGOFF_ACK) {
                    this.dataSubWasLoggedOff = true;
                    return;
                }
                this.dataSubWasLoggedOff = false;
                if (result.ROW) {
                    const nextMessage = dataServerResultFilter(result);
                    this.handleStreamUpdate(nextMessage);
                }
                else {
                    this.handleStreamUpdate(result);
                }
            });
        });
    }
    initDatasource() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.datasource.init(this.datasourceOptions(), true);
        });
    }
    datasourceOptions() {
        return {
            criteria: this.buildCriteria(),
            fields: this.fields,
            isSnapshot: this.isSnapshot,
            orderBy: this.orderBy,
            resourceName: this.resourceName,
            request: this.request,
            reverse: this.reverse,
            maxRows: this.maxRows,
            maxView: this.maxView,
        };
    }
    handleStreamUpdate(result) {
        if (!result)
            return;
        const isRequestServer = result.hasOwnProperty('REPLY');
        if (isRequestServer) {
            this.applyRequestServerData(result);
        }
        else {
            this.applyDataserverData(result);
        }
    }
    applyRequestServerData(requestServerResult) {
        var _a, _b;
        if (!Array.isArray(requestServerResult.REPLY) ||
            !((_a = requestServerResult.MESSAGE_TYPE) === null || _a === void 0 ? void 0 : _a.startsWith('REP_'))) {
            logger.error('received invalid RequestServerResult', requestServerResult);
            return;
        }
        if (this.requiresFullDataRefresh) {
            if (this.rowId !== 'ROW_REF') {
                (_b = requestServerResult.REPLY) === null || _b === void 0 ? void 0 : _b.forEach((insertData) => {
                    this.rowsSyncedWithStream.set(insertData[this.rowId], insertData);
                });
            }
            const rowData = this.rowsSyncedWithStream.size > 0
                ? Array.from(this.rowsSyncedWithStream.values())
                : requestServerResult.REPLY;
            this.syncComponentData(rowData);
            return;
        }
        this.handleStreamInserts(requestServerResult.REPLY);
        const rowsToDelete = Array.from(this.rowsSyncedWithStream.values()).filter((row) => !requestServerResult.REPLY.includes(row));
        this.handleStreamDeletes(rowsToDelete);
    }
    applyDataserverData(dataServerResult) {
        var _a, _b, _c, _d;
        if (this.requiresFullDataRefresh) {
            (_a = dataServerResult.inserts) === null || _a === void 0 ? void 0 : _a.forEach((insertData) => {
                this.rowsSyncedWithStream.set(insertData[this.rowId], insertData);
            });
            const rowData = Array.from(this.rowsSyncedWithStream.values());
            this.syncComponentData(rowData);
            return;
        }
        if (((_b = dataServerResult.inserts) === null || _b === void 0 ? void 0 : _b.length) > 0) {
            this.handleStreamInserts(dataServerResult.inserts);
        }
        if (((_c = dataServerResult.deletes) === null || _c === void 0 ? void 0 : _c.length) > 0) {
            this.handleStreamDeletes(dataServerResult.deletes);
        }
        if (((_d = dataServerResult.updates) === null || _d === void 0 ? void 0 : _d.length) > 0) {
            this.handleStreamUpdates(dataServerResult.updates);
        }
    }
    buildCriteria() {
        var _a;
        const initialCriteria = ((_a = this.criteria) === null || _a === void 0 ? void 0 : _a.split(criteriaDelimiter)) || [];
        return normaliseCriteria(initialCriteria.join(criteriaJoin), criteriaDelimiter);
    }
    setFilter(fieldName, newFilter) {
        this.criteriaFromFilters.set(fieldName, newFilter);
        this.update.next(this.criteriaFromFilters);
    }
    removeFilter(fieldName) {
        this.criteriaFromFilters.delete(fieldName);
        this.update.next(this.criteriaFromFilters);
    }
}
__decorate([
    Connect
], BaseDatasource.prototype, "connect", void 0);
__decorate([
    Datasource
], BaseDatasource.prototype, "datasource", void 0);
__decorate([
    attr({ mode: 'boolean' })
], BaseDatasource.prototype, "withGridInit", void 0);
__decorate([
    attr
], BaseDatasource.prototype, "criteria", void 0);
__decorate([
    attr
], BaseDatasource.prototype, "fields", void 0);
__decorate([
    attr({ mode: 'boolean' })
], BaseDatasource.prototype, "isSnapshot", void 0);
__decorate([
    attr
], BaseDatasource.prototype, "maxRows", void 0);
__decorate([
    attr
], BaseDatasource.prototype, "maxView", void 0);
__decorate([
    attr
], BaseDatasource.prototype, "orderBy", void 0);
__decorate([
    attr
], BaseDatasource.prototype, "resourceName", void 0);
__decorate([
    attr({ mode: 'boolean' })
], BaseDatasource.prototype, "reverse", void 0);
__decorate([
    attr
], BaseDatasource.prototype, "rowId", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'with-timestamp-formatting' })
], BaseDatasource.prototype, "withTimestampFormatting", void 0);
